import React from 'react';
import { LoginForm, AuthMethodSelect, AuthCode } from 'shared-portal-component';
import { useNavigate } from '@tanstack/react-router';
import { useSelector } from 'react-redux';

import { loginThunk } from '@/store/thunks/auth/loginThunk';
import { useAppDispatch } from '@/store';
import { select2FAMethodThunk } from '@/store/thunks/auth/select2FAMethodThunk';
import type { TwoFactorMethod } from '@/types/authState.types';
import { verify2faCodeThunk } from '@/store/thunks/auth/verify2faCodeThunk';
import { resend2faCodeThunk } from '@/store/thunks/auth/resend2faCodeThunk';
import { selectAuthError, selectAuthStatus, selectFieldError, selectLoginStep, selectMaskedEmail, selectMaskedPhone, selectTwoFactorMethod } from '@/store/selectors/auth';
import { setLoginStep } from '@/store/slices/auth/authSlice';

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // 🔑 get values directly from the store
  const step = useSelector(selectLoginStep);
  const status = useSelector(selectAuthStatus);
  const twoFactorMethod = useSelector(selectTwoFactorMethod);
  const fieldErrors = useSelector(selectFieldError('code'));
  const loading = status === 'loading';
  const error = useSelector(selectAuthError);
  const maskedEmail = useSelector(selectMaskedEmail);
  const maskedPhone = useSelector(selectMaskedPhone);

  // Handles login form submit
  const handleLogin = (values: { username: string; password: string }) => {
   void dispatch(loginThunk({ username: values.username, password: values.password }));
  };

  const handleForgotPassword = () => {
    void dispatch(select2FAMethodThunk({ method: twoFactorMethod }));
  };

  // Handles selection of method
  const handleSelectMethod = (method: TwoFactorMethod) => {
    void dispatch(select2FAMethodThunk({ method }));
  };

  // Handles code input submit
  const handleCodeSubmit = (code: string) => {
   void dispatch(verify2faCodeThunk({ code }))
      .unwrap()
      .then(() => {
        void navigate({ to: '/admin' }); // ✅ redirect after success
      });
  };

  const handleBackToMethod = () => {
    dispatch(setLoginStep('choose2fa'));
  };

  const handleResend = () => {
   void dispatch(resend2faCodeThunk({method: twoFactorMethod }));
  };

  // ---- RENDER LOGIC ----

  let content = null;
  switch (step) {
    case 'credentials':
      content = (
        <LoginForm
          onSubmit={handleLogin}
          onForgotPassword={handleForgotPassword}
          loading={loading}
          error={error}
        />
      );
      break;
    case 'choose2fa':
      content = (
        <AuthMethodSelect
          onSelect={handleSelectMethod}
          email={maskedEmail ?? ''}
          phone={maskedPhone ?? ''}
        />
      );
      break;
    case 'verify2fa':
      content = (
        <AuthCode
          method={twoFactorMethod}
          onBack={handleBackToMethod}
          onResend={handleResend}
          onSubmit={handleCodeSubmit}
          loading={loading}
          error={fieldErrors}
          masked={twoFactorMethod === 'email' ? (maskedEmail ?? '') : (maskedPhone ?? '')}
        />
      );
      break;
  }

  return <>{content}</>;
};

export default Login;
